import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authAPI } from '../services';
import { clearMobileCaches } from '../utils/mobileSessionCaches';

interface User {
  UserID?: string;
  UserName: string;
  CompanyID: string;
  CompanyName?: string;
  ProfileImage?: string;
  username?: string;
  usertype?: string;
  companyid?: string;
  [key: string]: any;
}

interface AuthContextType {
  user: User | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  loading: boolean;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is already logged in
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    
    
    if (token && savedUser) {
      try {
        const parsedUser = JSON.parse(savedUser);
        setUser(parsedUser);
      } catch (error) {
        console.error('Error parsing saved user:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
      // Refresh from the API in the background so server-side role or
      // permission changes take effect without forcing a re-login. The
      // cached user (set above) keeps the UI responsive meanwhile.
      authAPI
        .getCurrentUser()
        .then((freshUser) => {
          setUser(freshUser);
          localStorage.setItem('user', JSON.stringify(freshUser));
        })
        .catch(() => {
          // Offline or expired token: keep the cached user; API interceptors
          // handle real auth failures.
        });
    }
    setLoading(false);
  }, []);

  const login = async (username: string, password: string) => {
    // DON'T set loading state - let the component handle its own loading
    // setLoading(true);
    try {
      const response = await authAPI.login({ username, password });
      const { access_token } = response;
      
      localStorage.setItem('token', access_token);
      
      // Get user info from the API
      const userInfo = await authAPI.getCurrentUser();

      // A new login starts with empty in-memory pour caches (shared phones).
      clearMobileCaches();
      setUser(userInfo);
      localStorage.setItem('user', JSON.stringify(userInfo));
    } catch (error) {
      throw error;
    } finally {
      // DON'T reset loading state
      // setLoading(false);
    }
  };

  const logout = () => {
    // Drop the in-memory pour caches, so the next user of the device never
    // sees this user's data.
    clearMobileCaches();
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    authAPI.logout().catch(() => {
      // Ignore errors on logout
    });
  };

  const value = {
    user,
    login,
    logout,
    loading,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};